import styled from '@emotion/styled';
import type { PlanBadgeProps } from '@hotjar/hotsauce';
import { Icon, getInputContainerStyles, tokens } from '@hotjar/hotsauce';
import { useCallback, useMemo, useRef, Children } from 'react';
import type { AllBadgeStatus } from 'packages/hotsauce/build/components/Badge/BaseBadge';
import { useSyncSize } from './useSyncSize';
import { useListBoxControls } from './useListBoxControls';
import { useListBoxPosition } from './useListBoxPosition';
import { useScrollToActiveIndex } from './useScrollToActiveIndex';
import { ListBoxItem, SelectedValueContext } from './ListBoxItem';

export type { AdvancedOptionProps } from './AdvancedOption';

const SelectContainer = styled.div`
  display: flex;
  position: relative;
  max-width: 100%;
  user-select: none;
  width: auto;
`;

type SelectButtonProps = Parameters<typeof getInputContainerStyles>[0];

const SelectButton = styled.div<SelectButtonProps>`
  ${getInputContainerStyles}
  display: flex;
  align-items: center;
  height: ${tokens.spacer.x5};
  padding: 0;
  cursor: default;
  width: auto;

  border: none;
  ${tokens.transition()};
  ${tokens.elevation(1)}, inset 0 0 0 1px ${tokens.color.neutral_300};

  :hover {
    ${tokens.elevation(1)}, inset 0 0 0 1px ${tokens.color.neutral_500};
  }

  :focus {
    ${`${tokens.focus()}, inset 0 0 0 1px ${tokens.color.cobalt_800}`};
  }

  :focus-visible {
    outline: none;
  }
`;

const CaretIcon = styled(Icon)`
  flex-shrink: 0;
`;

const SelectContent = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${tokens.spacer.x1};
  padding: 0 ${tokens.spacer.x2};
  width: 100%;
`;

type ListBoxProps = { isOpen: boolean; position: 'above' | 'below' };
const ListBox = styled.div<ListBoxProps>`
  display: flex;
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  background: ${tokens.color.ui_background};
  border: 1px solid ${tokens.color.ui_border};
  ${tokens.elevation(1)};
  border-radius: ${tokens.radius.m};
  position: absolute;
  ${({ position }) =>
    position === 'above'
      ? `bottom: calc(100% + ${tokens.spacer.x1});`
      : `top: calc(100% + ${tokens.spacer.x1});`};

  flex-direction: column;
  white-space: nowrap;
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  max-width: 100%;
  min-width: 280px;
`;

const SelectionContainer = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

type Option = {
  props: {
    id: string;
    badge?: AllBadgeStatus;
    planBadge?: PlanBadgeProps['kind'];
    value: string;
    children: React.ReactNode;
  };
};

export type AdvancedSelectCommonProps = {
  children: React.ReactNode;
  selectedValue: string;
  id: string;
  maxWidth?: number | 'auto';
  onSelect: (value: string) => void;
  onMouseEnter?: () => void;
};

function isOption(child: React.ReactNode): child is Option {
  return child !== null && typeof child === 'object' && 'props' in child;
}

// props should always contain EITHER an ariaLabelledBy OR ariaLabel but never both
export type AdvancedSelectProps = AdvancedSelectCommonProps &
  ({ ariaLabelledBy: string; ariaLabel?: never } | { ariaLabel: string; ariaLabelledBy?: never });

export const AdvancedSelect = ({
  children,
  selectedValue,
  id,
  onSelect,
  onMouseEnter,
  maxWidth,
  ...rest
}: AdvancedSelectProps): JSX.Element => {
  const listBoxId = `${id}-listbox`;

  const buttonRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useSyncSize<HTMLDivElement, HTMLDivElement>({ sourceRef: listBoxRef, targetRef: contentRef });

  const options = useMemo(() => {
    return Children.toArray(children).filter(isOption);
  }, [children]);

  const selectValue = useCallback(
    (value: string) => {
      onSelect(value);
      return options.findIndex((child) => child.props.value === value);
    },
    [options, onSelect],
  );

  const selectIndex = useCallback(
    (index: number) => {
      onSelect(options[index].props.value);
    },
    [options, onSelect],
  );

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.props.value === selectedValue) ?? 0,
    [options, selectedValue],
  );

  const {
    isListBoxOpen,
    activeIndex,
    onButtonClick,
    onBlur,
    onKeyDown,
    onOptionMouseDown,
    onSelect: onListBoxSelect,
  } = useListBoxControls({
    optionsCount: options.length,
    selectedIndex,
    selectValue,
    selectIndex,
    buttonRef,
  });

  useScrollToActiveIndex({ listBoxRef, isListBoxOpen, activeIndex });

  const activeId = useMemo(() => options[activeIndex].props.id, [options, activeIndex]);

  const listBoxPosition = useListBoxPosition({ isListBoxOpen, listBoxRef, buttonRef, padding: 8 });

  const selectedOption = options[selectedIndex];

  return (
    <SelectContainer>
      <SelectButton
        ref={buttonRef}
        onBlur={onBlur}
        onClick={onButtonClick}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        id={id}
        role="combobox"
        aria-label={'ariaLabel' in rest ? rest.ariaLabel : undefined}
        aria-labelledby={'ariaLabelledBy' in rest ? rest.ariaLabelledBy : undefined}
        aria-expanded={isListBoxOpen}
        aria-controls={listBoxId}
        aria-haspopup="listbox"
        aria-activedescendant={isListBoxOpen ? activeId : ''}
        isInvalid={false}
        isDisabled={false}
        isReadonly={false}
        tabIndex={0}
        css={{ maxWidth }}
      >
        <SelectContent ref={contentRef}>
          <SelectionContainer>{selectedOption !== undefined && selectedOption}</SelectionContainer>
          <CaretIcon icon="caret_down_alt" color="text_light" />
        </SelectContent>
      </SelectButton>
      <ListBox
        onKeyDown={onKeyDown}
        ref={listBoxRef}
        id={listBoxId}
        role="listbox"
        isOpen={isListBoxOpen}
        position={listBoxPosition}
        aria-labelledby={'ariaLabelledBy' in rest ? rest.ariaLabelledBy : undefined}
        tabIndex={-1}
      >
        <SelectedValueContext.Provider
          // FIXME: This creates a new object on every render and may create unnecessary rerenders.
          //        Fix this or add a comment to explain why it's desired.
          //        More details: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-constructed-context-values.md
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            selectedValue,
            activeId,
            onSelect: onListBoxSelect,
            onOptionMouseDown,
          }}
        >
          {options.map((option) => (
            <ListBoxItem key={option.props.id} id={option.props.id} value={option.props.value}>
              {option}
            </ListBoxItem>
          ))}
        </SelectedValueContext.Provider>
      </ListBox>
    </SelectContainer>
  );
};
